import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Gift, Copy, Check, Coins, Users, Share2 } from "lucide-react";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load your referral info. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  const link = data ? `${window.location.origin}/register?ref=${data.referral_code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Try Videoileo", text: "Build faceless videos that grow your channel — join me on Videoileo:", url: link });
      } catch { /* user dismissed the share sheet */ }
    } else {
      copy();
    }
  };

  const converted = data?.converted ?? 0;
  const max = data?.max_friends ?? 5;
  const reward = data?.reward_per_friend ?? 100;

  if (loading) {
    return (
      <div className="space-y-8" data-testid="referrals-page">
        <div className="h-24 rounded-md bg-white/5 animate-pulse" />
        <div className="h-28 rounded-md bg-white/5 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-md bg-white/5 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="referrals-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Refer &amp; earn</p>
        <h1 className="font-head text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Gift className="h-8 w-8 text-emerald-400" /> Give credits, get credits
        </h1>
        <p className="text-zinc-400 mt-2 max-w-xl">
          Earn <span className="text-white font-semibold">{reward} credits</span> for every friend who becomes a paying Videoileo customer — up to <span className="text-white font-semibold">{max} friends</span> ({reward * max} credits total).
        </p>
      </div>

      {/* Share link */}
      <div className="border border-white/10 rounded-md bg-[#121212] p-5">
        <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Your referral link</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            readOnly
            value={link}
            data-testid="referral-link-input"
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2.5 text-sm text-zinc-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button onClick={copy} data-testid="copy-referral-btn" className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors">
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
          </button>
          <button onClick={share} data-testid="share-referral-btn" className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors">
            <Share2 className="h-4 w-4" /> Share
          </button>
        </div>
        <p className="text-xs text-zinc-600 mt-2">Code: <span className="font-mono text-zinc-400" data-testid="referral-code">{data?.referral_code || "…"}</span></p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-white/10 rounded-md bg-[#121212] p-5">
          <Coins className="h-5 w-5 text-emerald-400 mb-3" />
          <p className="font-head text-3xl font-bold" data-testid="referral-credits-earned">{data?.credits_earned ?? 0}</p>
          <p className="text-xs text-zinc-500 mt-1">Credits earned</p>
        </div>
        <div className="border border-white/10 rounded-md bg-[#121212] p-5">
          <Users className="h-5 w-5 text-blue-400 mb-3" />
          <p className="font-head text-3xl font-bold" data-testid="referral-converted">{converted}<span className="text-lg text-zinc-500"> / {max}</span></p>
          <p className="text-xs text-zinc-500 mt-1">Friends converted</p>
        </div>
        <div className="border border-white/10 rounded-md bg-[#121212] p-5">
          <Gift className="h-5 w-5 text-fuchsia-400 mb-3" />
          <p className="font-head text-3xl font-bold" data-testid="referral-remaining">{data?.remaining ?? max}</p>
          <p className="text-xs text-zinc-500 mt-1">Rewards remaining</p>
        </div>
      </div>

      {/* Progress */}
      <div className="border border-white/10 rounded-md bg-[#121212] p-5">
        <div className="flex items-center gap-1.5 mb-4">
          {Array.from({ length: max }).map((_, i) => (
            <div key={`pip-${i}`} className={`h-2 flex-1 rounded-full transition-colors ${i < converted ? "bg-emerald-500" : "bg-white/10"}`} data-testid={`referral-pip-${i}`} />
          ))}
        </div>
        <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3">Your invites</p>
        {data?.friends?.length ? (
          <div className="space-y-2" data-testid="referral-friends-list">
            {data.friends.map((f, i) => (
              <div key={`${f.name}-${i}`} className="flex items-center justify-between border border-white/10 rounded-md px-3 py-2.5 bg-[#0A0A0A]">
                <span className="text-sm">{f.name}</span>
                {f.converted ? (
                  <span className="text-xs inline-flex items-center gap-1 text-emerald-400"><Check className="h-3.5 w-3.5" /> Converted · +{reward}</span>
                ) : (
                  <span className="text-xs text-zinc-500">Joined — not yet subscribed</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-zinc-500" data-testid="referral-empty">No invites yet. Share your link to start earning!</p>
        )}
      </div>

      {/* How it works */}
      <div className="border border-white/10 rounded-md bg-[#121212] p-5 text-sm text-zinc-400 space-y-2">
        <p className="text-zinc-200 font-semibold">How it works</p>
        <p>1. Share your unique link with creators who'd love faceless video.</p>
        <p>2. They sign up using your link and subscribe to any paid plan.</p>
        <p>3. You instantly get <span className="text-white">{reward} credits</span> — up to {max} friends ({reward * max} credits).</p>
      </div>
    </div>
  );
}
